import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod/v3'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { AuthorizedDeleteConfirmButton } from '~/components/new/buttons/delete-confirm-button'
import { CameraViewForm } from '~/components/routes/cameras/camera-views/camera-view-form'
import { CAMERA_STREAM_DELETE, CAMERA_STREAM_EDIT } from '~/components/routes/cameras/permissions'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/cameras-views/$viewId/edit')({
  staticData: {
    permission: CAMERA_STREAM_EDIT,
  },
  component: CameraViewEditPage,
  validateSearch: z.object({
    returnTo: z.enum(['legacy', 'new']).optional(),
  }),
})
withRoutePermissions(Route)

function CameraViewEditPage() {
  const { viewId } = Route.useParams()
  const { returnTo } = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const viewQuery = useQuery(api.cameraViews.show.queryOptions({ params: { id: viewId } }))

  const invalidateCameraViews = () => {
    void queryClient.invalidateQueries(api.cameraViews.list.queryFilter())
    void queryClient.invalidateQueries(api.cameraViews.show.queryFilter({ params: { id: viewId } }))
  }

  const updateCameraView = useMutation(
    api.cameraViews.update.mutationOptions({ onSuccess: invalidateCameraViews }),
  )
  const deleteCameraView = useMutation(
    api.cameraViews.destroy.mutationOptions({ onSuccess: invalidateCameraViews }),
  )

  const view = viewQuery.data ?? null

  if (!view) {
    return (
      <>
        <AppHeader>
          <Title>
            {viewQuery.isError ? 'Vue caméras introuvable' : 'Chargement de la vue caméras…'}
          </Title>
        </AppHeader>
        <AppContainer>
          {viewQuery.isError ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
              Cette vue n'existe pas ou a été supprimée.
            </div>
          ) : null}
        </AppContainer>
      </>
    )
  }

  const handleDelete = () => {
    deleteCameraView.mutate(
      { params: { id: view.id } },
      {
        onSuccess: () => {
          if (returnTo === 'new') {
            navigate({ to: '/cameras/new', search: { mode: 'views' } })
          } else {
            navigate({ to: '/cameras-views', search: {} })
          }
        },
      },
    )
  }

  return (
    <>
      <AppHeader>
        <div className="flex w-full items-center justify-between gap-4">
          <Title>Modifier « {view.name} »</Title>
          <AuthorizedDeleteConfirmButton
            permission={CAMERA_STREAM_DELETE}
            itemLabel={view.name}
            dialogTitle="Supprimer la vue caméras"
            onConfirm={handleDelete}
          />
        </div>
      </AppHeader>

      <AppContainer className="flex-1 min-h-0">
        <CameraViewForm
          key={view.id}
          submitPermission={CAMERA_STREAM_EDIT}
          defaultValues={view}
          onSave={(values) => {
            updateCameraView.mutate(
              { params: { id: view.id }, body: values },
              {
                onSuccess: (updated) => {
                  if (returnTo === 'new') {
                    navigate({
                      to: '/cameras/new',
                      search: { mode: 'views', viewId: updated.id },
                    })
                  } else {
                    navigate({ to: '/cameras-views', search: { viewId: updated.id } })
                  }
                },
              },
            )
          }}
          onCancel={() => {
            if (returnTo === 'new') {
              navigate({
                to: '/cameras/new',
                search: { mode: 'views', viewId: view.id },
              })
            } else {
              navigate({ to: '/cameras-views', search: { viewId: view.id } })
            }
          }}
        />
      </AppContainer>
    </>
  )
}
